import { ConfigProvider, Flex, Typography } from "antd";
import { ITEM_TYPES, TabKeys, TemplateType } from "../types";
import { useAntTheme } from "hooks/useAntTheme";
import { StyledMenuTabs } from "./StyledMenuTabs";
import { PanelContent } from "./StoryboardMenuTabs/PanelContent";
import { useTemplateData } from "../hooks/useTemplateData";
import { useTemplateEditorStore } from "../store/useTemplateEditorStore";
import { useTemplatesStatusStore } from "../store/useTemplatesStatusStore";
import { ScriptTab } from "./StoryboardMenuTabs/ScriptTab";
import { LayoutTab } from "./StoryboardMenuTabs/LayoutTab";
import { ContentTab } from "./StoryboardMenuTabs/ContentTab";
import { ObjectsTab } from "./StoryboardMenuTabs/ObjectsTab";
import { BrandTab } from "./StoryboardMenuTabs/BrandTab";
import { AvatarTab } from "./StoryboardMenuTabs/AvatarTab";
import { MotionTab } from "./StoryboardMenuTabs/MotionTab";
import { AudioTab } from "./StoryboardMenuTabs/AudioTab";
import { useTranslate } from "@refinedev/core";
import { useItemCopyPaste } from "../hooks/useItemCopyPaste";
import { tabMatchesItem } from "../helpers/itemTypeToTab";

export const StoryboardMenu = ({
  previewWidth,
  previewHeight,
}: {
  previewWidth: number;
  previewHeight: number;
}) => {
  const { theme } = useAntTheme();
  const t = useTranslate();

  const {
    templatesData: { presenter: initialPresenter },
    brandKitData,
  } = useTemplateData();

  const { brandKit, brandKitPalette } = brandKitData;

  const {
    currentItem,
    setCurrentItem,
    currentChapter,
    table,
    selectedItemIds,
  } = useTemplateEditorStore((store) => ({
    currentItem: store.templateState.currentItem,
    setCurrentItem: store.setCurrentItem,
    currentChapter: store.currentChapter,
    table: store.templateState.table,
    selectedItemIds: store.selectedItemIds,
  }));

  const { activeTab, setActiveTab } = useTemplatesStatusStore((state) => ({
    activeTab: state.activeTab,
    setActiveTab: state.setActiveTab,
  }));

  // Own the Cmd/Ctrl+C / Cmd/Ctrl+V item copy/paste shortcut for the video
  // editor (previously handled by the now-removed settings toolbar).
  useItemCopyPaste({
    previewWidth,
    previewHeight,
    registerShortcuts: true,
  });

  const tabsItems = [
    {
      key: TabKeys.script,
      label: t("components.templates.settings.scriptTab.tabTitle"),
      disableScroll: false,
      child: <ScriptTab />,
    },
    {
      key: TabKeys.layout,
      label: t("components.templates.settings.layoutTab.tabTitle"),
      disableScroll: false,
      child: <LayoutTab />,
    },
    {
      key: TabKeys.content,
      label: t("components.templates.settings.contentTab.tabTitle"),
      disableScroll: false,
      child: (
        <ContentTab previewHeight={previewHeight} previewWidth={previewWidth} />
      ),
    },
    {
      key: TabKeys.objects,
      label: t("components.templates.settings.objectsTab.tabTitle"),
      disableScroll: true,
      child: (
        <ObjectsTab previewWidth={previewWidth} previewHeight={previewHeight} />
      ),
    },
    {
      key: TabKeys.brand,
      label: t("components.templates.settings.brandTab.tabTitle"),
      disableScroll: false,
      child: (
        <BrandTab
          previewWidth={previewWidth}
          previewHeight={previewHeight}
          brandKit={brandKit?.data}
        />
      ),
    },
    {
      key: TabKeys.avatar,
      label: t("components.templates.settings.avatarTab.tabTitle"),
      disableScroll: false,
      child: (
        <AvatarTab
          initialPresenter={initialPresenter}
          brandKitPalette={brandKitPalette}
          previewWidth={previewWidth}
          previewHeight={previewHeight}
        />
      ),
    },
    {
      key: TabKeys.motion,
      label: t("components.templates.settings.motionTab.tabTitle"),
      disableScroll: false,
      child: <MotionTab />,
    },
    {
      key: TabKeys.audio,
      label: t("components.templates.settings.audioTab.tabTitle"),
      disableScroll: true,
      child: <AudioTab />,
    },
  ].map((item) => ({
    key: `${item.key}`,
    label: (
      <Flex
        vertical
        justify="center"
        align="center"
        style={{ width: "100%", fontSize: 12 }}
      >
        {item.label}
      </Flex>
    ),
    children: (
      <PanelContent disableScroll={item.disableScroll}>
        {item.child}
      </PanelContent>
    ),
  }));

  // A group selection has no single item to configure — show a placeholder
  // instead of the per-item settings tabs.
  if (selectedItemIds.length > 1) {
    return (
      <PanelContent>
        <Flex
          align="center"
          justify="center"
          style={{ height: "100%", padding: 24, textAlign: "center" }}
        >
          <Typography.Text type="secondary">
            {t("components.templates.settings.multipleItemsSelected", {
              count: selectedItemIds.length,
            })}
          </Typography.Text>
        </Flex>
      </PanelContent>
    );
  }

  return (
    <ConfigProvider
      theme={{
        components: {
          Tabs: {
            itemColor: theme.colorTextDescription,
            itemSelectedColor: theme.colorTextDescription,
            itemHoverColor: theme.colorPrimary,
            titleFontSize: 11,
            inkBarColor: "transparent",
          },
        },
      }}
    >
      <Flex
        vertical
        style={{
          overflow: "hidden",
          width: "100%",
          height: "100%",
          // Solid surface so the translucent colorBgContainerDisabled tab strip
          // blends over it (not the page), matching the left panel accordions.
          backgroundColor: theme.colorBgContainer,
        }}
      >
        <StyledMenuTabs
          tabPosition="left"
          items={tabsItems}
          activeKey={activeTab ?? undefined}
          onChange={(tab) => {
            if (!isValidTabKey(tab)) return;

            setActiveTab(tab);
            if (
              currentChapter.type === TemplateType.CHAPTER &&
              tab === TabKeys.brand
            ) {
              if (currentItem?.type !== ITEM_TYPES.Logo) {
                setCurrentItem({ type: ITEM_TYPES.Logo });
              }
            } else if (
              currentChapter.type === TemplateType.CHAPTER &&
              tab === TabKeys.avatar
            ) {
              if (currentItem?.type !== ITEM_TYPES.Presenter) {
                setCurrentItem({ type: ITEM_TYPES.Presenter });
              }
            } else if (tab === TabKeys.content && table) {
              // Auto-select the table when the table section is visible so its
              // alignment/position controls are active immediately.
              if (currentItem?.type !== ITEM_TYPES.Table) {
                setCurrentItem({ type: ITEM_TYPES.Table, id: table.id });
              }
            } else if (
              tab !== TabKeys.motion &&
              !tabMatchesItem(tab, currentItem?.type)
            ) {
              setCurrentItem(undefined);
            }
          }}
          tabBarGutter={0}
          tabBarStyle={{
            backgroundColor: theme.colorBgContainerDisabled,
          }}
          variant="card"
        />
      </Flex>
    </ConfigProvider>
  );
};

const isValidTabKey = (tab: string): tab is TabKeys => {
  return Object.values(TabKeys).includes(tab as TabKeys);
};
